import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosClientSignIn } from "../axios/api";
import { getSocket } from '../socket/socket';

// ============= ASYNC THUNKS =============

// Fetch all threads for user
export const fetchAllThreads = createAsyncThunk(
  'thread/fetchAllThreads',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get('/chat/threads');
      return response.data.threads;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch specific thread conversation
export const fetchThreadConversation = createAsyncThunk(
  'thread/fetchThreadConversation',
  async (messageId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/chat/thread/${messageId}`);
      return {
        messageId,
        threadData: response.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Send thread message via socket (for real-time)
export const sendThreadMessage = createAsyncThunk(
  'thread/sendThreadMessage',
  async ({ threadId, content, userId }, { rejectWithValue }) => {
    try {
      const socket = getSocket();
      if (!socket) {
        throw new Error('Socket not available');
      }

      const message = {
        sender: userId,
        threadId,
        content
      };

      socket.emit('sendThreadMessage', message);
      return message;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============= INITIAL STATE =============
const initialState = {
  // Thread lists and data
  threads: [], // All user threads from /threads API
  currentThread: null, // Currently viewing thread
  threadConversations: {}, // Cache: { messageId: threadData }
  
  // Loading states
  loading: {
    fetchingThreads: false,
    fetchingConversation: false,
    sendingMessage: false
  },
  
  // Error handling
  error: {
    threads: null,
    conversation: null,
    sendMessage: null
  },
  
  // Thread typing indicators
  threadTyping: {
    // { threadId: { userId: { senderName, isTyping, timestamp } } }
  },
  
  // Thread notifications
  threadNotifications: {
    unreadCounts: {}, // { threadId: count }
    latestMessages: {}, // { threadId: lastMessage }
    notifications: [] // Array of thread notifications
  },
  
  // UI state
  ui: {
    selectedThreadId: null,
    showNotifications: false,
    notificationSound: true,
    activeThreadView: 'list' // 'list' | 'conversation'
  }
};

// ============= THREAD SLICE =============
const threadSlice = createSlice({
  name: 'thread',
  initialState,
  reducers: {
    // ===== THREAD MANAGEMENT =====
    setCurrentThread: (state, action) => {
      state.currentThread = action.payload;
      state.ui.selectedThreadId = action.payload?.threadId || null;
    },

    clearCurrentThread: (state) => {
      state.currentThread = null;
      state.ui.selectedThreadId = null;
    },

    updateThreadConversation: (state, action) => {
      const { messageId, newMessage } = action.payload;
      if (state.threadConversations[messageId]) {
        state.threadConversations[messageId].messages.push(newMessage);
      }
    },

    // ===== TYPING INDICATORS =====
    setThreadTyping: (state, action) => {
      const { threadId, userId, senderName, isTyping } = action.payload;
      console.log(`🔤 Thread Redux: Setting typing for ${senderName} (${userId}) in thread ${threadId}: ${isTyping}`);
      
      if (!state.threadTyping[threadId]) {
        state.threadTyping[threadId] = {};
      }
      
      if (isTyping) {
        state.threadTyping[threadId][userId] = {
          senderName,
          isTyping: true,
          timestamp: Date.now()
        };
      } else {
        delete state.threadTyping[threadId][userId];
        
        // Clean up empty thread objects
        if (Object.keys(state.threadTyping[threadId]).length === 0) {
          delete state.threadTyping[threadId];
        }
      }
    },

    clearThreadTyping: (state, action) => {
      const { threadId, userId } = action.payload;
      if (state.threadTyping[threadId]) {
        delete state.threadTyping[threadId][userId];
        
        if (Object.keys(state.threadTyping[threadId]).length === 0) {
          delete state.threadTyping[threadId];
        }
      }
    },

    clearAllThreadTyping: (state) => {
      state.threadTyping = {};
    },

    // ===== NOTIFICATIONS =====
    addThreadNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        timestamp: Date.now(),
        ...action.payload
      };
      state.threadNotifications.notifications.unshift(notification);
      
      // Keep only last 50 notifications
      if (state.threadNotifications.notifications.length > 50) {
        state.threadNotifications.notifications = state.threadNotifications.notifications.slice(0, 50);
      }
    },

    updateThreadUnreadCount: (state, action) => {
      console.log(action.payload,"action.payloadaction.payloadaction.payloadaction.payloadaction.payloadaction.payload")
      const { threadId, count, lastMessage,mainThreadId } = action.payload;
      state.threadNotifications.unreadCounts[mainThreadId] = count;
      
      if (lastMessage) {
        state.threadNotifications.latestMessages[threadId] = lastMessage;
      }
    },

    clearThreadUnreadCount: (state, action) => {
      const { threadId } = action.payload;
      state.threadNotifications.unreadCounts[threadId] = 0;
    },

    markThreadNotificationAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.threadNotifications.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.isRead = true;
      }
    },

    clearThreadNotifications: (state) => {
      state.threadNotifications.notifications = [];
    },

    // ===== UI MANAGEMENT =====
    setActiveThreadView: (state, action) => {
      state.ui.activeThreadView = action.payload; // 'list' | 'conversation'
    },

    toggleNotificationSound: (state) => {
      state.ui.notificationSound = !state.ui.notificationSound;
    },

    setShowNotifications: (state, action) => {
      state.ui.showNotifications = action.payload;
    },

    // ===== REAL-TIME MESSAGE HANDLING =====
    receiveThreadMessage: (state, action) => {
      const message = action.payload;
      const { threadId } = message;
      
      // Add to current conversation if it's loaded
      const conversationKey = Object.keys(state.threadConversations).find(key => 
        state.threadConversations[key].threadId === threadId
      );
      
      if (conversationKey && state.threadConversations[conversationKey]) {
        state.threadConversations[conversationKey].messages.push(message);
      }
      
      // Update latest message
      state.threadNotifications.latestMessages[threadId] = {
        content: message.content,
        senderName: message.sender?.firstName || 'Unknown',
        timestamp: message.createdAt || new Date().toISOString()
      };
      
      // Increment unread count if not current thread
      if (state.ui.selectedThreadId !== threadId) {
        const currentCount = state.threadNotifications.unreadCounts[threadId] || 0;
        state.threadNotifications.unreadCounts[threadId] = currentCount + 1;
        
        // Add notification
        const notification = {
          id: Date.now() + Math.random(),
          type: 'thread_message',
          threadId,
          title: `New reply in thread`,
          message: `${message.sender?.firstName}: ${message.content}`,
          timestamp: Date.now(),
          isRead: false
        };
        
        state.threadNotifications.notifications.unshift(notification);
      }
    },

    // ===== ERROR HANDLING =====
    clearThreadError: (state, action) => {
      const errorType = action.payload;
      if (errorType && state.error[errorType]) {
        state.error[errorType] = null;
      }
    },

    clearAllThreadErrors: (state) => {
      state.error = {
        threads: null,
        conversation: null,
        sendMessage: null
      };
    }
  },

  // ============= EXTRA REDUCERS (ASYNC THUNKS) =============
  extraReducers: (builder) => {
    builder
      // Fetch All Threads
      .addCase(fetchAllThreads.pending, (state) => {
        state.loading.fetchingThreads = true;
        state.error.threads = null;
      })
      .addCase(fetchAllThreads.fulfilled, (state, action) => {
        state.loading.fetchingThreads = false;
        state.threads = action.payload;
      })
      .addCase(fetchAllThreads.rejected, (state, action) => {
        state.loading.fetchingThreads = false;
        state.error.threads = action.payload;
      })

      // Fetch Thread Conversation
      .addCase(fetchThreadConversation.pending, (state) => {
        state.loading.fetchingConversation = true;
        state.error.conversation = null;
      })
      .addCase(fetchThreadConversation.fulfilled, (state, action) => {
        state.loading.fetchingConversation = false;
        const { messageId, threadData } = action.payload;
        
        // Cache the conversation
        state.threadConversations[messageId] = threadData;
        
        // Set as current thread
        state.currentThread = threadData;
        state.ui.selectedThreadId = threadData.threadId;
        
        // Clear unread count for this thread
        state.threadNotifications.unreadCounts[threadData.threadId] = 0;
      })
      .addCase(fetchThreadConversation.rejected, (state, action) => {
        state.loading.fetchingConversation = false;
        state.error.conversation = action.payload;
      })

      // Send Thread Message
      .addCase(sendThreadMessage.pending, (state) => {
        state.loading.sendingMessage = true;
        state.error.sendMessage = null;
      })
      .addCase(sendThreadMessage.fulfilled, (state, action) => {
        state.loading.sendingMessage = false;
        // Message will be added via receiveThreadMessage when socket confirms
      })
      .addCase(sendThreadMessage.rejected, (state, action) => {
        state.loading.sendingMessage = false;
        state.error.sendMessage = action.payload;
      });
  }
});

// ============= ACTIONS EXPORT =============
export const {
  setCurrentThread,
  clearCurrentThread,
  updateThreadConversation,
  setThreadTyping,
  clearThreadTyping,
  clearAllThreadTyping,
  addThreadNotification,
  updateThreadUnreadCount,
  clearThreadUnreadCount,
  markThreadNotificationAsRead,
  clearThreadNotifications,
  setActiveThreadView,
  toggleNotificationSound,
  setShowNotifications,
  receiveThreadMessage,
  clearThreadError,
  clearAllThreadErrors
} = threadSlice.actions;

// ============= SELECTORS =============
export const selectAllThreads = (state) => state.thread.threads;
export const selectCurrentThread = (state) => state.thread.currentThread;
export const selectThreadConversation = (messageId) => (state) => state.thread.threadConversations[messageId];
export const selectThreadTyping = (threadId) => (state) => state.thread.threadTyping[threadId] || {};
export const selectThreadNotifications = (state) => state.thread.threadNotifications.notifications;
export const selectUnreadThreadCount = (threadId) => (state) => state.thread.threadNotifications.unreadCounts[threadId] || 0;
export const selectTotalUnreadThreads = (state) => {
  return Object.values(state.thread.threadNotifications.unreadCounts).reduce((total, count) => total + count, 0);
};
export const selectThreadLoading = (state) => state.thread.loading;
export const selectThreadErrors = (state) => state.thread.error;

export default threadSlice.reducer;
